import { FunctionComponent, MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { faEye } from "@fortawesome/pro-solid-svg-icons";

import { Icon } from "@/components";

import { AnchoredPopover } from "../LeadPanelActions/AnchoredPopover";
import { TrackerPickerPanel } from "../LeadPanelActions/TrackerPickerPanel";
import { useLeadPanelActions } from "../LeadPanelActions/useLeadPanelActions";

interface AddToTrackerButtonProps {
	prospectId: number;
	prospectName: string;
	portalContainer?: HTMLElement | null;
}

/**
 * The Recent box's "Add to Profile Tracker" CTA. Opens the same {@link TrackerPickerPanel} the
 * "Add to ▾" menu uses (a tracker must be chosen to add to); the pick commits via the shared
 * {@link useLeadPanelActions} `addToTracker`. Extension-safe via `portalContainer`.
 */
export const AddToTrackerButton: FunctionComponent<AddToTrackerButtonProps> = ({
	prospectId,
	prospectName,
	portalContainer,
}) => {
	const { t } = useTranslation();
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const actions = useLeadPanelActions({ prospectId, prospectName });

	const toggle = (event: MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		setAnchorEl((current) => (current ? null : event.currentTarget));
	};

	return (
		<>
			<button
				className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-brand-light px-2.5 py-1.5 text-xs font-semibold text-white duration-200 hover:shadow-md"
				data-testid="lead-panel-recent-add-tracker"
				type="button"
				onClick={toggle}
			>
				<Icon className="w-3" icon={faEye} />
				{t("contacts.leadPanel.recent.addToTracker")}
			</button>

			{anchorEl && (
				<AnchoredPopover
					anchorEl={anchorEl}
					portalContainer={portalContainer}
					width={288}
					onClose={() => setAnchorEl(null)}
				>
					<TrackerPickerPanel
						isLoading={actions.isAddingToTracker}
						onSelectTracker={(tracker) => {
							void actions.addToTracker(tracker.id);
							setAnchorEl(null);
						}}
					/>
				</AnchoredPopover>
			)}

			{actions.renderSnackbar()}
		</>
	);
};
